// Static content for the share-link declaration wizard, transcribed from the
// client prototype (chirpyest_share_link_flows.html). Copy is inlined here as
// data; if/when this is internationalised, move these strings to locale files.

export type Segment = 'influencer' | 'digital_marketer' | 'business'

export const SEGMENTS: {
  id: Segment
  title: string
  description: string
}[] = [
  {
    id: 'influencer',
    title: 'Influencer / creator',
    description: 'Content creators and social media personalities',
  },
  {
    id: 'digital_marketer',
    title: 'Digital marketer',
    description: 'Marketing professionals & growth teams',
  },
  {
    id: 'business',
    title: 'Business',
    description: 'Brands, companies & organisations',
  },
]

// Influencer social handle fields
export const INFLUENCER_SOCIALS: {
  id: string
  label: string
  prefix: string
}[] = [
  { id: 'instagram', label: 'Instagram', prefix: '@' },
  { id: 'tiktok', label: 'TikTok', prefix: '@' },
  { id: 'facebook', label: 'Facebook', prefix: 'fb.com/' },
  { id: 'youtube', label: 'YouTube', prefix: '@' },
]

// Digital marketer: prohibited activities the user must acknowledge
export const PROHIBITED_ACTIVITIES: {
  id: string
  title: string
  details: string[]
}[] = [
  {
    id: 'sem',
    title: 'Paid search / SEM violations',
    details: [
      'Bidding on retailer brand names, misspellings, or trademarked terms in any search engine (e.g. "Wayfair coupon", "Amazn")',
      'Brand-plus-coupon bidding that inserts you at final checkout (e.g. "Sephora promo code")',
    ],
  },
  {
    id: 'hij',
    title: 'Ad hijacking & impersonation',
    details: [
      'Ad copy or branding that mimics or impersonates a retailer; display URL hijacking (e.g. brandname-sale.com)',
      'Deceptive landing pages that intercept users already navigating to a retailer',
    ],
  },
  {
    id: 'attr',
    title: 'Attribution manipulation & cookie fraud',
    details: [
      'Cookie stuffing via hidden iframes, invisible pixels, or forced redirects; click injection before conversion',
      'Last-click interception via coupon popups, cashback plugins, or browser extensions',
    ],
  },
  {
    id: 'cpn',
    title: 'Coupon, deal & forced-click abuse',
    details: [
      'Publishing expired, invalid, or fabricated coupon codes; auto-firing affiliate tracking on page view or silent redirect',
      'Browser extension overwrites that poach existing affiliate attribution at checkout',
    ],
  },
]

export const PROHIBITED_ACTIVITY_IDS = PROHIBITED_ACTIVITIES.map(p => p.id)

export const TRAFFIC_SOURCES: {
  id: 'soc' | 'net' | 'both'
  title: string
  description: string
}[] = [
  {
    id: 'soc',
    title: 'Social media only',
    description:
      'Meta, TikTok, Pinterest, Snapchat, Reddit. No other ad networks.',
  },
  {
    id: 'net',
    title: 'Ad network / traffic platform',
    description: 'Google Ads, Meta Ads, PropellerAds, Taboola, or similar',
  },
  {
    id: 'both',
    title: 'Both social media and ad networks',
    description: 'I use a mix of social platforms and other traffic sources',
  },
]

export const MARKETER_SOCIAL_PLATFORMS: { id: string; label: string }[] = [
  { id: 'meta', label: 'Meta / Facebook' },
  { id: 'tiktok', label: 'TikTok Ads' },
  { id: 'pinterest', label: 'Pinterest Ads' },
  { id: 'snapchat', label: 'Snapchat Ads' },
  { id: 'reddit', label: 'Reddit Ads' },
]

// Commonly known ad networks / traffic platforms offered as selectable tiles.
export const AD_NETWORKS: string[] = [
  'Google Ads',
  'Meta Ads',
  'Microsoft / Bing Ads',
  'Taboola',
  'Outbrain',
  'Amazon Ads',
]

// Validates a website/blog URL. Rejects plain strings with no domain/TLD.
export const isValidWebUrl = (value: string): boolean => {
  const v = (value || '').trim()
  if (!v) return false
  return /^(https?:\/\/)?([\w-]+\.)+[a-z]{2,}(:\d{1,5})?(\/\S*)?$/i.test(v)
}

// Business types
export const BUSINESS_TYPES: { id: string; label: string }[] = [
  { id: 'media', label: 'Media / publisher' },
  { id: 'styling', label: 'Styling & fashion' },
  { id: 'interior', label: 'Interior design' },
  { id: 'realestate', label: 'Real estate' },
  { id: 'beauty', label: 'Beauty & makeup' },
  { id: 'retail', label: 'Retail / storefront' },
  { id: 'procurement', label: 'Procurement / corporate' },
  { id: 'agency', label: 'Marketing agency' },
  { id: 'events', label: 'Events & hospitality' },
  { id: 'other', label: 'Other' },
]

export const BUSINESS_USE_CASES: { id: string; label: string }[] = [
  { id: 'clients', label: 'Sharing directly with clients or customers' },
  {
    id: 'storefront',
    label: 'Embedding in a website, storefront, or lookbook',
  },
  {
    id: 'spreadsheet',
    label: 'Spreadsheet, procurement list, or internal tool',
  },
  { id: 'social', label: 'Social media posts or stories' },
  { id: 'email', label: 'Email newsletters or client communications' },
  { id: 'cashback', label: 'To earn cash back on business purchases' },
  { id: 'other', label: 'Other' },
]

export const BUSINESS_TYPE_TIPS: Record<string, string> = {
  media:
    'e.g. We publish editorial content and embed product links within articles and gift guides.',
  styling:
    "e.g. I style clients and send curated product links to shop looks I've put together.",
  interior:
    'e.g. I share product links with clients in my design proposals and shopping lists.',
  realestate:
    'e.g. I share home furnishing and staging links with buyers and sellers.',
  beauty:
    'e.g. I recommend products to clients after appointments and share links via messages.',
  retail:
    'e.g. I embed product links in my shop pages for customers to purchase through.',
  procurement:
    'e.g. Our team shares approved product lists via internal spreadsheets.',
  agency:
    'e.g. We manage affiliate campaigns and distribute links across client channels.',
  events:
    'e.g. We share product recommendations for decor, styling, and gifting.',
  other: 'Briefly describe how your business will use Chirpyest share links.',
}

export const AFFILIATE_POLICY_URL =
  'https://www.chirpyest.com/company#terms:~:text=search%20engine%20marketing,pending%20cash%20back'

// Per-segment post-declaration review notice shown on the success screen.
export const REVIEW_NOTICE: Record<
  Segment,
  { heading: string; body: string; bullets: string[] }
> = {
  influencer: {
    heading: 'Your share link is live, and your account is being reviewed',
    body:
      "As part of our standard onboarding, we're taking a quick look at your account. We may reach out if we need a little more information to fully verify your profile. Most accounts are reviewed within 1 to 2 business days.",
    bullets: [
      'Your share link is active and ready to use right now',
      'You can browse, shop, and share as normal while we review',
      "We'll email you if we need anything from you",
    ],
  },
  business: {
    heading: "Your share link is ready, and we're getting to know you",
    body:
      "As part of our onboarding process, we're reviewing your business details. We may reach out to learn more about how you plan to use Chirpyest. Most businesses are reviewed within 1 to 2 business days.",
    bullets: [
      'Your share link is active and ready to use now',
      'You can share links with clients and customers right away',
      "We'll email you if we need anything from you",
    ],
  },
  digital_marketer: {
    heading: 'Your share link is active, pending final verification',
    body:
      'You can start using your share link right away. As a digital marketer your account is subject to a more thorough compliance review. We will be verifying your declared platforms and traffic sources and may contact you for additional information. Share links may be paused if we are unable to verify your account within the review period.',
    bullets: [
      'Share link is active and can be used now',
      'Compliance review typically takes 2 to 3 business days',
      'We will email you directly if we need anything',
    ],
  },
}
